import { mapGlobalSearchToSearchPage } from '@disco/utils/global-search'
import { getMusicProviderFromSearchQuery } from '@disco/utils/links'
import { checkSetFilters, normalizeSearchTerm } from '@disco/utils/search'
import {
  cleanUrlQuery,
  isAiSearchType,
  mapBrowseSortToSearchPage,
} from '@disco/utils/search-page'

import type { GetState, State } from 'types/redux'
import type { SearchType, V2SearchQuery } from 'types/search-page'
import type {
  AppliedSearch,
  SearchFilters,
} from '@disco/state-management/reducers/search'
import {
  clearBrowseSearchedLast,
  clearResults,
  clearSearch as clearSearchPage,
  fetchLinkInfo,
  fetchResults,
  resetAiSorting,
  selectBrowseAiMode,
  selectBrowseFilterState,
  selectBrowseSimSearchUrl,
  selectBrowseTypeAfterPromptSearch,
  selectIsSearchFiltersInBrowseEnabled,
  setAiMode,
  setBrowseAiMode,
  setQuery,
  setSearchType,
  setSorting,
} from '@disco/state-management/reducers/search-page'
import { getExplorerFirstPane } from '@disco/state-management/selectors/explorer'

import { getSearchResults, updateSearchParams } from './SearchActions'

type SearchInput = { query: string; searchType: SearchType }

const EMPTY_APPLIED_SEARCH: AppliedSearch = {
  searchTerm: '',
  groups: [],
  groupObjects: [],
  tags: [],
  nativeTags: [],
  filters: [],
  startDate: new Date(0),
  endDate: null,
  duration: null,
  filterState: null,
}

const SEARCH_TYPES_BY_API_TYPE: Record<V2SearchQuery['type'], SearchType> = {
  regular: 'keyword',
  llm: 'prompt',
  simsearch: 'similarity',
}

// How the search page runs a Browse term, so a link or a prompt carries over as one.
const toSearchPageInput = (state: State, term: string): SearchInput => {
  const simSearchUrl = selectBrowseSimSearchUrl(state, term)
  if (simSearchUrl) return { query: simSearchUrl, searchType: 'similarity' }
  // The table would strip a link out of a prompt query
  const isPromptSearch =
    selectBrowseAiMode(state) === 'active' &&
    !getMusicProviderFromSearchQuery(term)
  if (term && isPromptSearch) {
    return { query: term, searchType: 'prompt' }
  }
  return { query: term, searchType: 'keyword' }
}

// The search the page last ran; its input can hold text that hasn't been searched yet.
const getSearchPageSubmittedInput = (state: State): SearchInput | null => {
  const submitted: V2SearchQuery | null = state.searchPage.submittedSearch
  // A newer search is loading or failed, so this one is no longer what the page shows
  if (!submitted || !state.searchPage.submittedSearchIsLatest) return null
  return {
    query: submitted.query,
    searchType: SEARCH_TYPES_BY_API_TYPE[submitted.type],
  }
}

const isSameSearch = (a: SearchInput, b: SearchInput | null): boolean =>
  b !== null &&
  a.searchType === b.searchType &&
  normalizeSearchTerm(cleanUrlQuery(a.query, a.searchType)) ===
    normalizeSearchTerm(cleanUrlQuery(b.query, b.searchType))

/**
 * Moves Browse's last search to the search page: the term, its filters, and
 * whether it ran as a prompt or a link search.
 * @param useLegacySearch - false from a Browse library pane, which has no search to carry
 * @param browseSort - the sort Browse's results were fetched with, kept for a prompt or link search
 */
export const carryBrowseSearchToSearchPage =
  (useLegacySearch = true, browseSort?: string[]) =>
  (dispatch: (...args: Array<any>) => any, getState: GetState): void => {
    const state = getState()
    const source =
      (useLegacySearch && state.search.appliedSearch) || EMPTY_APPLIED_SEARCH
    const transfer = mapGlobalSearchToSearchPage({
      searchTerm: source.searchTerm,
      groups: source.groups,
      tags: source.tags,
      nativeTags: source.nativeTags,
      filters: source.filters,
      startDate: source.startDate ?? null,
      endDate: source.endDate ?? null,
      duration: source.duration ?? null,
      metadataKeys: useLegacySearch ? state.search.metadataKeys : [],
      groupObjects: source.groupObjects,
    })
    const filters = useLegacySearch
      ? source.filterState ?? transfer.filters
      : selectBrowseFilterState(state) ?? []
    const { query, searchType } = toSearchPageInput(state, transfer.query)

    dispatch(clearSearchPage(filters))
    dispatch(setSorting([]))
    dispatch(resetAiSorting())
    // setSearchType clears linkInfo, so it has to run before fetchLinkInfo
    dispatch(setSearchType(searchType))
    dispatch(setQuery(query))
    // After setSearchType: setSorting writes the AI sort only once the type is prompt or similarity
    if (isAiSearchType(searchType)) {
      dispatch(setSorting(mapBrowseSortToSearchPage(browseSort)))
    }
    if (searchType === 'similarity') dispatch(fetchLinkInfo(query))
    // For a link, the search bar switches the toggle on itself where prompt search is available
    dispatch(setAiMode(searchType === 'prompt' ? 'active' : 'off'))
    dispatch(clearResults())

    // The page fetches on first mount; subsequent visits need an explicit refresh.
    if (state.searchPage.initialFetchLoaded) dispatch(fetchResults())
  }

/**
 * STL-1278: the sidebar's Search link. Brings Browse's search over, unless the
 * search page has searched since. When Browse has no term or the same one, the
 * page keeps its own search and sorting and only refreshes for the shared filters.
 */
export const carryNewerBrowseSearchToSearchPage =
  () =>
  (dispatch: (...args: Array<any>) => any, getState: GetState): void => {
    const state = getState()
    if (!selectIsSearchFiltersInBrowseEnabled(state)) return
    if (!state.searchPage.browseSearchedLast) return

    const term = state.search.appliedSearch?.searchTerm ?? ''
    const browseInput = toSearchPageInput(state, term)
    if (
      term &&
      !isSameSearch(browseInput, getSearchPageSubmittedInput(state))
    ) {
      const browseSort = getExplorerFirstPane(state)?.searchQueryV2?.sort
      dispatch(carryBrowseSearchToSearchPage(true, browseSort))
      return
    }

    // The page only fetches on its first mount, so it would keep results from before the filter change
    if (state.searchPage.initialFetchLoaded) dispatch(fetchResults())
  }

// The search page's search as Browse can run it, or null when Browse can't.
const getBrowseTermFromSearchPage = (
  state: State,
): { searchTerm: string; isPromptSearch: boolean } | null => {
  const submitted = getSearchPageSubmittedInput(state)
  if (!submitted?.query.trim()) return null
  // Browse only runs links it recognises, e.g. not the stream URL behind "Find similar"
  if (
    submitted.searchType === 'similarity' &&
    !selectBrowseSimSearchUrl(state, submitted.query)
  ) {
    return null
  }
  return {
    searchTerm: submitted.query,
    isPromptSearch: submitted.searchType === 'prompt',
  }
}

// Same as Browse's toggle: prompt search locks the type to Tracks until it ends.
const getBrowseTypeLock = (
  state: State,
  isPromptSearch: boolean,
): Pick<SearchFilters, 'type'> | null => {
  if (isPromptSearch) return { type: 'track' }
  if (selectBrowseAiMode(state) !== 'active') return null
  return { type: selectBrowseTypeAfterPromptSearch(state) }
}

/**
 * STL-1278: the sidebar's Browse link from the search page. Brings the page's
 * search over, or reruns Browse's own so its results match the filters edited there.
 * @param teamLibraryId - the library the Browse link opens
 * @returns false when there's no search to return to, so the caller resets Browse instead
 */
export const carrySearchPageSearchToBrowse =
  (teamLibraryId: number) =>
  (dispatch: (...args: Array<any>) => any, getState: GetState): boolean => {
    const state = getState()
    // Null while the Browse filters flag is off
    const browseFilterState = selectBrowseFilterState(state)
    if (browseFilterState === null) return false

    const search = getBrowseTermFromSearchPage(state)
    if (search) {
      const typeLock = getBrowseTypeLock(state, search.isPromptSearch)
      // A link switches Browse's toggle on by itself once its chip loads
      dispatch(setBrowseAiMode(search.isPromptSearch ? 'active' : 'off'))
      dispatch(
        updateSearchParams({
          searchTerm: search.searchTerm,
          searchOpen: true,
          ...typeLock,
        }),
      )
    } else {
      const { selectedLibrary } = state.app
      const hasBrowseSearch = checkSetFilters(state.search, browseFilterState, {
        ignoreType: true,
      })
      // A channel still selected needs resetting to the team library; a Browse search leaves it null
      const isOnTeamLibrary =
        !selectedLibrary || selectedLibrary.id === teamLibraryId
      if (!hasBrowseSearch || !isOnTeamLibrary) return false
    }

    dispatch(getSearchResults())
    // The rerun isn't a new Browse search, so it mustn't replace the search page's own on the way back
    dispatch(clearBrowseSearchedLast())
    return true
  }
